// QA observability (operator request 2026-08-08): the QA account was seeing
// client-side failures the server logs could not explain — error toasts,
// intermittent send failures, forced secure-session resets, slow thread
// loads. On ALLOWLISTED accounts only, batch-ship debug events to the server
// (POST /api/v1/client_debug_logs; the server enforces its own allowlist, so
// this client gate is purely to avoid useless traffic for everyone else).
//
// Fire-and-forget by design: observability must never affect the behavior it
// observes. Failures are swallowed; the queue is bounded.

import { apiRequest } from 'mastodon/api';
import { PERMISSION_MANAGE_SETTINGS } from 'mastodon/permissions';

const QA_USERNAMES = ['testaccount'];

const FLUSH_INTERVAL_MS = 4_000;
const MAX_QUEUE = 200; // beyond this, oldest events drop — never grow unbounded
const MAX_PER_FLUSH = 50; // server-side request cap

interface QaEvent {
  at: string;
  kind: string;
  message: string;
  detail?: string;
}

let enabled: boolean | null = null;
const queue: QaEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function detectEnabled(): boolean {
  if (enabled !== null) return enabled;
  try {
    const el = document.getElementById('initial-state');
    const state: unknown = el ? JSON.parse(el.textContent) : null;
    const s = state as {
      meta?: { me?: string };
      role?: { permissions?: number | string };
      accounts?: Record<string, { username?: string } | undefined>;
    } | null;
    const meId = s?.meta?.me;
    let username: string | undefined;
    if (meId) username = s.accounts?.[meId]?.username;
    // Admins ship debug events too (operator request 2026-08-10): staff hit
    // bugs in the wild that the QA account never sees. Same bitmask check as
    // useIdentity(); the server re-checks with its own role gate.
    const rolePermissions = Number(s?.role?.permissions ?? 0);
    const isAdmin =
      (rolePermissions & PERMISSION_MANAGE_SETTINGS) ===
      PERMISSION_MANAGE_SETTINGS;
    enabled = isAdmin || (!!username && QA_USERNAMES.includes(username));
  } catch {
    enabled = false;
  }
  return enabled;
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_PER_FLUSH);
  try {
    await apiRequest('POST', 'v1/client_debug_logs', {
      data: { events: batch },
    });
  } catch {
    // Outage events are the most valuable ones (e.g. a 521 means the server
    // never saw the request) — REQUEUE the batch and retry after a longer
    // pause once connectivity returns. MAX_QUEUE still bounds memory: if the
    // outage outlasts the buffer, oldest events fall off.
    queue.unshift(...batch);
    while (queue.length > MAX_QUEUE) queue.pop();
    flushTimer = setTimeout(() => void flush(), FLUSH_INTERVAL_MS * 5);
    return;
  }
  if (queue.length > 0) scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => void flush(), FLUSH_INTERVAL_MS);
}

/**
 * Record a QA debug event. No-op for non-QA accounts. `detail` is truncated
 * server-side; keep it to the useful facts (status codes, ids, durations).
 */
export function qaLog(kind: string, message: string, detail?: string): void {
  if (!detectEnabled()) return;
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push({
    at: new Date().toISOString(),
    kind,
    message: message.slice(0, 500),
    ...(detail ? { detail: detail.slice(0, 2000) } : {}),
  });
  scheduleFlush();
}

/** Compress an unknown error into a loggable string. */
export function qaErrorDetail(err: unknown): string {
  const e = err as {
    message?: string;
    response?: { status?: number; data?: unknown };
    config?: { method?: string; url?: string };
  } | null;
  const parts: string[] = [];
  if (e?.config?.method || e?.config?.url)
    parts.push(`${e.config.method ?? ''} ${e.config.url ?? ''}`.trim());
  if (e?.response?.status) parts.push(`status=${e.response.status}`);
  if (e?.response?.data)
    parts.push(`body=${JSON.stringify(e.response.data).slice(0, 300)}`);
  if (e?.message) parts.push(`err=${e.message}`);
  return parts.join(' ') || String(err);
}
